import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Phone, Clock, MapPin, CheckCircle, Star, Users, 
  Home, Sun, Moon, Car, Coffee, Shield, Smile, Activity,
  Calendar, Loader2, FileText, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service data
const companionServices = [
  "Friendly companionship and conversation",
  "Sitting and supervision",
  "Reading, puzzles, cards, board games, crafts, and activities",
  "Watching favorite shows or movies together",
  "Social engagement",
  "Giving family caregivers time to rest and recharge"
];

const outingServices = [
  "Going outside for fresh air",
  "Walks and outdoor activities",
  "Parks and community outings",
  "Shopping trips",
  "Lunch or short recreational outings",
  "Accompanying clients to approved activities",
  "Short trips away from home with caregiver authorization"
];

const transportServices = [
  "Scheduled pickup and drop-off",
  "Transportation for approved outings",
  "Errands and appointments when arranged in advance",
  "Transportation based on scheduling and availability"
];

const householdServices = [
  "Light cleaning and tidying",
  "Washing dishes",
  "Straightening common areas",
  "Light laundry",
  "Making the bed",
  "Taking out household trash",
  "Helping maintain a comfortable and safe environment"
];

const mealServices = [
  "Preparing simple meals and snacks",
  "Serving meals and beverages",
  "Encouraging hydration",
  "Supporting normal daily routines",
  "Non-medical reminders as appropriate"
];

// Care shift options
const shiftOptions = [
  { value: "day-short", label: "Short Respite Visit (2-4 hours)", time: "Daytime" },
  { value: "day-half", label: "Half-Day Care (4-6 hours)", time: "Daytime" },
  { value: "day-full", label: "Full-Day Care (7:00 AM - 7:00 PM)", time: "Daytime" },
  { value: "night", label: "Overnight Respite (7:00 PM - 7:00 AM)", time: "Night Shift" }
];

// Relationship options
const relationshipOptions = [
  "Spouse/Partner",
  "Parent",
  "Grandparent", 
  "Sibling",
  "Aunt/Uncle",
  "Friend/Neighbor",
  "Other Family Member",
  "Other"
];

// Safety requirements that may apply
const safetyRequirements = [
  { id: "emergency_contact", label: "Emergency contact information" },
  { id: "allergies", label: "Known allergies and dietary needs" },
  { id: "mobility", label: "Mobility and safety information" },
  { id: "transport_consent", label: "Transportation consent" },
  { id: "outing_auth", label: "Outing authorization" },
  { id: "medical_auth", label: "Emergency medical authorization" },
  { id: "liability", label: "Liability and service agreements" }
];

export default function LifeRestCare() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Caregiver/Family Contact Info
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    relationship: '',
    
    // Client Information
    clientName: '',
    clientAge: '',
    
    // Care Preferences
    shiftType: '',
    preferredDate: '',
    preferredTime: '',
    address: '',
    
    // Client Needs
    mobilityInfo: '',
    dietaryNeeds: '',
    specialInstructions: '',
    activitiesPreferred: '',
    
    // Additional Info
    emergencyContact: '',
    emergencyPhone: '',
    additionalNotes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit registration request
      await axios.post(`${API}/bookings/liferest`, {
        ...formData,
        submittedAt: new Date().toISOString()
      });
      
      // Also send contact notification
      await axios.post(`${API}/contact`, {
        name: formData.contactName,
        email: formData.contactEmail,
        subject: `LifeRest Care Registration - ${formData.clientName}`,
        message: `
LIFEREST CARE SERVICES REGISTRATION

Contact Information:
- Name: ${formData.contactName}
- Email: ${formData.contactEmail}
- Phone: ${formData.contactPhone}
- Relationship to Client: ${formData.relationship}

Client Information:
- Client Name: ${formData.clientName}
- Client Age: ${formData.clientAge}

Care Request:
- Shift Type: ${formData.shiftType}
- Preferred Date: ${formData.preferredDate}
- Preferred Time: ${formData.preferredTime}
- Service Address: ${formData.address}

Client Needs:
- Mobility Info: ${formData.mobilityInfo || 'Not specified'}
- Dietary Needs: ${formData.dietaryNeeds || 'Not specified'}
- Preferred Activities: ${formData.activitiesPreferred || 'Not specified'}
- Special Instructions: ${formData.specialInstructions || 'None'}

Emergency Contact:
- Name: ${formData.emergencyContact}
- Phone: ${formData.emergencyPhone}

Additional Notes:
${formData.additionalNotes || 'None'}
        `.trim()
      });

      toast.success('Registration submitted successfully! We will contact you within 24 hours to discuss your care needs.');
      
      // Reset form
      setFormData({
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        relationship: '',
        clientName: '',
        clientAge: '',
        shiftType: '',
        preferredDate: '',
        preferredTime: '',
        address: '',
        mobilityInfo: '',
        dietaryNeeds: '',
        specialInstructions: '',
        activitiesPreferred: '',
        emergencyContact: '',
        emergencyPhone: '',
        additionalNotes: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit registration. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#2d5a87] via-[#3d6a97] to-[#4d7aa7] py-20 md:py-28">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cGF0aCBkPSJNLTEwIDMwaDYwdi0yMGgtNjB6IiBmaWxsLW9wYWNpdHk9Ii4wNCIgZmlsbD0iI2ZmZiIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
                Non-Medical Respite Care
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                LifeRest Care Services
              </h1>
              <p className="text-2xl md:text-3xl text-blue-100 mb-4 font-light italic">
                Rest Easy. We're Here.
              </p>
              <p className="text-lg text-blue-100/90 mb-8 leading-relaxed">
                LifeRest Care Services provides non-medical respite and companion care designed to give family caregivers time to rest, work, run errands, attend appointments, spend time with family, or simply take care of themselves.
              </p>
              <p className="text-blue-100/80 mb-8">
                Your loved one receives meaningful companionship, supervision, activities, and support while you take a much-needed break.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#register">
                  <Button size="lg" className="bg-white text-[#2d5a87] hover:bg-blue-50 rounded-full shadow-lg" data-testid="get-started-btn">
                    Register for Care
                  </Button>
                </a>
                <a href="tel:912-388-6305">
                  <Button size="lg" variant="outline" className="text-white border-white/50 hover:bg-white/10 rounded-full" data-testid="call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    912-388-6305
                  </Button>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/7551684/pexels-photo-7551684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Caring companion with senior"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2d5a87]/50 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2d5a87] to-[#4d7aa7] rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Compassionate Care</p>
                  <p className="text-sm text-gray-500">20+ Years Experience</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex gap-4 items-start"
          >
            <div className="flex-shrink-0">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-amber-900 mb-3">Important Service Notice</h2>
              <p className="text-amber-800 mb-4">
                LifeRest Care Services is a <strong>non-medical</strong> companion and respite care service.
                We do not provide skilled nursing, medical treatment, medical procedures, or services that require a licensed medical professional.
              </p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {["Companionship", "Supervision", "Respite support", "Light household assistance", "Activities & social engagement", "Meal & snack assistance"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-amber-800">
                    <CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-amber-700 mt-4 italic">
                Any service outside our non-medical scope will not be performed unless LifeRest becomes appropriately licensed and authorized to provide that service.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-[#2d5a87]/10 text-[#2d5a87] border-[#2d5a87]/20">Our Services</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Non-Medical Care
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From companionship to light household help, we're here to support your loved one while giving you peace of mind.
            </p>
          </motion.div>

          <div className="space-y-16">
            {/* Companion Care */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              <div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://images.pexels.com/photos/7551684/pexels-photo-7551684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Companion Care"
                    className="w-full h-[300px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-500 to-pink-600 opacity-20" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 text-white mb-4">
                  <Heart className="w-5 h-5" />
                  <span className="font-medium">Companion & Respite Care</span>
                </div>
                <ul className="space-y-3">
                  {companionServices.map((service) => (
                    <li key={service} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2d5a87] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Outings */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              <div className="lg:order-2">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://images.pexels.com/photos/12013184/pexels-photo-12013184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Outings"
                    className="w-full h-[300px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-600 opacity-20" />
                </div>
              </div>
              <div className="lg:order-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white mb-4">
                  <Sun className="w-5 h-5" />
                  <span className="font-medium">Outings & Adventures</span>
                </div>
                <ul className="space-y-3">
                  {outingServices.map((service) => (
                    <li key={service} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2d5a87] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Transportation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              <div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://images.unsplash.com/photo-1559234938-b60fff04894d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxwYXJrJTIwd2FsayUyMG91dGRvb3IlMjBhY3Rpdml0eSUyMHNlbmlvcnN8ZW58MHx8fHwxNzkwMTAyNjgyfDA&ixlib=rb-4.1.0&q=85"
                    alt="Transportation"
                    className="w-full h-[300px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-20" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white mb-4">
                  <Car className="w-5 h-5" />
                  <span className="font-medium">Transportation</span>
                </div>
                <ul className="space-y-3">
                  {transportServices.map((service) => (
                    <li key={service} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2d5a87] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Household Help */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              <div className="lg:order-2">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://images.unsplash.com/photo-1768511731493-d3c2aa483bb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxob21lJTIwY29tZm9ydCUyMGxpdmluZyUyMHJvb20lMjBjb3p5fGVufDB8fHx8MTc5MDEwMjY4Mnww&ixlib=rb-4.1.0&q=85"
                    alt="Household Help"
                    className="w-full h-[300px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 opacity-20" />
                </div>
              </div>
              <div className="lg:order-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white mb-4">
                  <Home className="w-5 h-5" />
                  <span className="font-medium">Light Household Help</span>
                </div>
                <ul className="space-y-3">
                  {householdServices.map((service) => (
                    <li key={service} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2d5a87] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Meal Support */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              <div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://images.pexels.com/photos/6633412/pexels-photo-6633412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Meal Support"
                    className="w-full h-[300px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-violet-600 opacity-20" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-violet-600 text-white mb-4">
                  <Coffee className="w-5 h-5" />
                  <span className="font-medium">Meal & Daily Routine Support</span>
                </div>
                <ul className="space-y-3">
                  {mealServices.map((service) => (
                    <li key={service} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2d5a87] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Transportation Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-200"
          >
            <div className="flex gap-3 items-start">
              <Car className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <p className="text-blue-800 text-sm">
                <strong>Transportation Note:</strong> Transportation and outings require appropriate family or responsible-party authorization. 
                Additional consent, emergency-contact, transportation, and liability paperwork may be required before transportation services are provided.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Flexible Care Options */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-[#2d5a87]/10 text-[#2d5a87] border-[#2d5a87]/20">Flexible Scheduling</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Care Options That Fit Your Needs
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    <Sun className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-gray-900">Day Shift</CardTitle>
                  <CardDescription className="text-lg text-amber-700 font-medium">7:00 AM – 7:00 PM</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Short respite visits, half-day care, full-day care, and extended daytime care may be scheduled based on availability.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                    <Moon className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-gray-900">Night Shift / Overnight</CardTitle>
                  <CardDescription className="text-lg text-indigo-700 font-medium">7:00 PM – 7:00 AM</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Overnight care is available by advance request and based on caregiver availability.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-gray-500 mt-8"
          >
            Pickup and drop-off times may also be arranged according to the family's needs and LifeRest availability.
          </motion.p>
        </div>
      </section>

      {/* Purpose Section */}
      <section className="py-20 bg-gradient-to-br from-[#2d5a87] to-[#3d6a97] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-16 h-16 mx-auto mb-6 text-blue-200" />
            <h2 className="text-3xl md:text-4xl font-bold mb-8">The Purpose of LifeRest</h2>
            <div className="space-y-6 text-lg text-blue-100">
              <p>Sometimes caregivers need a few hours.</p>
              <p>Sometimes they need the entire day.</p>
              <p>Sometimes they need a full night of uninterrupted rest.</p>
            </div>
            <p className="text-xl text-white mt-8 font-medium">
              LifeRest steps in for a short period of time so caregivers can take care of themselves while knowing their loved one has companionship, attention, activities, supervision, and support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Safety & Requirements */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Safety First</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Safety & Client Information
            </h2>
            <p className="text-gray-600">
              Before services begin, LifeRest may require the following information to provide consistent, organized, and safe non-medical support:
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 md:grid-cols-3 gap-4"
          >
            {[
              "Emergency contact information",
              "Authorized pickup and drop-off persons",
              "Known allergies and dietary needs",
              "Mobility and safety information",
              "Transportation consent",
              "Outing authorization",
              "Emergency medical authorization",
              "Liability and service agreements",
              "Client routines and special instructions"
            ].map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
              >
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{item}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-[#2d5a87]/10 text-[#2d5a87] border-[#2d5a87]/20">Get Started</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Register for LifeRest Care
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Fill out the form below to tell us about your care needs. We'll contact you within 24 hours to discuss how we can help.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-2 border-[#2d5a87]/20 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#2d5a87]" />
                      Your Contact Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contactName">Your Name *</Label>
                        <Input
                          id="contactName"
                          value={formData.contactName}
                          onChange={(e) => handleInputChange('contactName', e.target.value)}
                          placeholder="Enter your full name"
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactEmail">Email *</Label>
                        <Input
                          id="contactEmail"
                          type="email"
                          value={formData.contactEmail}
                          onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                          placeholder="your@email.com"
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactPhone">Phone *</Label>
                        <Input
                          id="contactPhone"
                          type="tel"
                          value={formData.contactPhone}
                          onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                          placeholder="(912) 555-0123"
                          required
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="relationship">Relationship to Client *</Label>
                        <Select value={formData.relationship} onValueChange={(v) => handleInputChange('relationship', v)} required>
                          <SelectTrigger data-testid="relationship-select">
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            {relationshipOptions.map((rel) => (
                              <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Client Information */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Heart className="w-5 h-5 text-[#2d5a87]" />
                      Client Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="clientName">Client's Name *</Label>
                        <Input
                          id="clientName"
                          value={formData.clientName}
                          onChange={(e) => handleInputChange('clientName', e.target.value)}
                          placeholder="Name of person receiving care"
                          required
                          data-testid="client-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientAge">Client's Age *</Label>
                        <Input
                          id="clientAge"
                          type="number"
                          value={formData.clientAge}
                          onChange={(e) => handleInputChange('clientAge', e.target.value)}
                          placeholder="Age"
                          required
                          data-testid="client-age-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Care Preferences */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#2d5a87]" />
                      Care Preferences
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="shiftType">Type of Care Needed *</Label>
                        <Select value={formData.shiftType} onValueChange={(v) => handleInputChange('shiftType', v)} required>
                          <SelectTrigger data-testid="shift-type-select">
                            <SelectValue placeholder="Select care type" />
                          </SelectTrigger>
                          <SelectContent>
                            {shiftOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label} ({opt.time})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="preferredDate">Preferred Start Date</Label>
                        <Input
                          id="preferredDate"
                          type="date"
                          value={formData.preferredDate}
                          onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                          data-testid="preferred-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="preferredTime">Preferred Time</Label>
                        <Input
                          id="preferredTime"
                          type="time"
                          value={formData.preferredTime}
                          onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                          data-testid="preferred-time-input"
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="address">Service Address *</Label>
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          placeholder="Full address where care will be provided"
                          required
                          data-testid="address-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Client Needs */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#2d5a87]" />
                      Client Needs & Preferences
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="mobilityInfo">Mobility Information</Label>
                        <Textarea
                          id="mobilityInfo"
                          value={formData.mobilityInfo}
                          onChange={(e) => handleInputChange('mobilityInfo', e.target.value)}
                          placeholder="Describe any mobility aids, transfer assistance needs, fall risks, etc."
                          rows={2}
                          data-testid="mobility-info-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dietaryNeeds">Dietary Needs & Allergies</Label>
                        <Textarea
                          id="dietaryNeeds"
                          value={formData.dietaryNeeds}
                          onChange={(e) => handleInputChange('dietaryNeeds', e.target.value)}
                          placeholder="List any food allergies, dietary restrictions, or preferences"
                          rows={2}
                          data-testid="dietary-needs-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="activitiesPreferred">Preferred Activities</Label>
                        <Textarea
                          id="activitiesPreferred"
                          value={formData.activitiesPreferred}
                          onChange={(e) => handleInputChange('activitiesPreferred', e.target.value)}
                          placeholder="What activities does the client enjoy? (e.g., reading, puzzles, walks, TV shows)"
                          rows={2}
                          data-testid="activities-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="specialInstructions">Special Instructions</Label>
                        <Textarea
                          id="specialInstructions"
                          value={formData.specialInstructions}
                          onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                          placeholder="Any specific routines, preferences, or important information we should know"
                          rows={3}
                          data-testid="special-instructions-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-[#2d5a87]" />
                      Emergency Contact
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emergencyContact">Emergency Contact Name *</Label>
                        <Input
                          id="emergencyContact"
                          value={formData.emergencyContact}
                          onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                          placeholder="Emergency contact name"
                          required
                          data-testid="emergency-contact-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emergencyPhone">Emergency Contact Phone *</Label>
                        <Input
                          id="emergencyPhone"
                          type="tel"
                          value={formData.emergencyPhone}
                          onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                          placeholder="(912) 555-0123"
                          required
                          data-testid="emergency-phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="additionalNotes">Additional Notes</Label>
                    <Textarea
                      id="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                      placeholder="Any other information you'd like to share"
                      rows={3}
                      data-testid="additional-notes-input"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#2d5a87] to-[#4d7aa7] hover:from-[#1d4a77] hover:to-[#3d6a97] text-white py-6 text-lg rounded-xl shadow-lg"
                    data-testid="submit-registration-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Heart className="w-5 h-5 mr-2" />
                        Submit Registration
                      </>
                    )}
                  </Button>

                  <p className="text-center text-sm text-gray-500">
                    By submitting this form, you agree to be contacted by LifeRest Care Services to discuss your care needs.
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-gradient-to-r from-[#2d5a87] via-[#3d6a97] to-[#4d7aa7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              LifeRest Care Services
            </h2>
            <p className="text-xl text-blue-100 mb-2">
              Non-Medical Respite • Companion Care • Outings • Transportation • Overnight Support
            </p>
            <p className="text-2xl font-light italic text-blue-200">
              Rest Easy. We're Here.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
